import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import { ActivityType, ONLINE_ENCOUNTER, SOCIAL_CLUB } from 'api/classroom/ClassroomState'
import { ClassDetails, getClassDetails, getClassResult, restoreRatingsFromClassResult } from 'api/unit-assessment'
import { Student } from 'api/unit-assessment/class-details'
import { Guid } from 'api/unit-assessment/types'
import { BookingContext } from 'contexts/booking'
import { ConferenceContext } from 'contexts/conference'
import { AssessmentStatus, BookingStatus, type StudentLevelInfoFetchStateValue } from 'utils/constants'
import type { StudentLevelInfo } from 'utils/level-unit-helpers'
import { getUnitAssessment, UnitAssessment } from './get-unit-assessment'

export { useSavingStatus } from './useSavingStatus'
export type { SavingCallbacks, SavingStatus } from './useSavingStatus'
export { useSaveScore } from './useSaveScore'
export { ASSESSMENT_RECOMMENDATIONS_FEATURE_FLAG } from 'utils/constants'
export type { Score } from './useSaveScore'
export type { ActivityRating, TaskRating } from './group-ratings'
export type { UnitAssessment }

export type StudentWithScores = Student & {
    scores: Map<Guid, number>
    finalScore?: number
    comment: string
    contentItemResultType?: number | undefined
    isGraded: boolean
    isScoreOrResultChanged: boolean
    isScoreAndResultAvailableInAPI: boolean
    isNoShowTechResultsSelected: boolean
    isActivitiesCommentsDisabled: boolean
    recommendLevelChange: boolean
    recommendAdvSession: boolean
    levelChangeRecommendation: string
    advSessionRecommendation: string
    studentLevelInfo?: StudentLevelInfo
    studentLevelInfoFetchState?: StudentLevelInfoFetchStateValue
}
export type AssessmentState = {
    status: typeof AssessmentStatus.WAITING_FOR_SESSION_JOIN
} | {
    status: typeof AssessmentStatus.CLASS_DETAILS_LOADED
    activityType: ActivityType
    classDetails: ClassDetails
    students: StudentWithScores[]
} | {
    unitAssessment: UnitAssessment
    activityType: ActivityType
    classDetails: ClassDetails
    students: StudentWithScores[]
    status: typeof AssessmentStatus.ENCOUNTER
} | {
    activityType: ActivityType
    classDetails: ClassDetails
    students: StudentWithScores[]
    status: typeof AssessmentStatus.NON_ENCOUNTER
} | {
    status: typeof AssessmentStatus.STUDENT
}
export const AssessmentContext = createContext<AssessmentState>({
    status: AssessmentStatus.WAITING_FOR_SESSION_JOIN
})

export const Assessment: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState<AssessmentState>({
        status: AssessmentStatus.WAITING_FOR_SESSION_JOIN
    })

    const conference = useContext(ConferenceContext)
    const booking = useContext(BookingContext)

    useEffect(() => {
        const isNseSession = sessionStorage.getItem('isTeensClass') !== '' && sessionStorage.getItem('isTeensClass') !== undefined
        if (conference.status === 'session-joined' && booking.status === BookingStatus.VALIDATED && isNseSession) {
            if (state.status === AssessmentStatus.WAITING_FOR_SESSION_JOIN) {
                if (booking.user.role === 'teacher') {
                    void getClassDetails(booking.auth, conference.classroomStateOnJoin.id).then(classDetails => {
                        const students: StudentWithScores[] = classDetails.bookedStudents.map(booking => ({
                            firstName: booking.student.firstName,
                            lastName: booking.student.lastName,
                            photoUris: booking.student.photoUris,
                            scores: new Map<string, number>(),
                            userId: booking.student.userId,
                            comment: '',
                            isGraded: false,
                            isScoreOrResultChanged: false,
                            isScoreAndResultAvailableInAPI: false,
                            isNoShowTechResultsSelected: false,
                            isActivitiesCommentsDisabled: false,
                            recommendLevelChange: false,
                            recommendAdvSession: false,
                            levelChangeRecommendation: '',
                            advSessionRecommendation: ''
                        }))

                        void getClassResult(booking.auth, classDetails.classId).then(classResult => {
                            restoreRatingsFromClassResult(classResult, students)

                            setState({
                                activityType: conference.classroomStateOnJoin.activityType,
                                status: AssessmentStatus.CLASS_DETAILS_LOADED,
                                classDetails: {
                                    ...classDetails,
                                    isClosed: classResult.isClosed
                                },
                                students
                            })
                        })
                    })

                } else {
                    setState({ status: AssessmentStatus.STUDENT })
                }

            } else if (state.status === AssessmentStatus.CLASS_DETAILS_LOADED) {
                const unit = conference.classroomStateOnJoin.unit
                if (state.activityType === ONLINE_ENCOUNTER) {
                    if (unit) {
                        void getUnitAssessment(booking.auth, process.env.REACT_APP_ASSESSMENT_UNIT_ID ?? unit.id).then(unitAssessment => {
                            setState(current => {
                                if (current.status === AssessmentStatus.CLASS_DETAILS_LOADED) {
                                    return {
                                        classDetails: current.classDetails,
                                        activityType: current.activityType,
                                        students: current.students,
                                        status: AssessmentStatus.ENCOUNTER,
                                        unitAssessment
                                    }
                                } else {
                                    return current
                                }
                            })
                        })
                    }

                } else if (state.activityType === SOCIAL_CLUB) {
                    setState(current => {
                        if (current.status === AssessmentStatus.CLASS_DETAILS_LOADED) {
                            return {
                                classDetails: current.classDetails,
                                activityType: current.activityType,
                                students: current.students,
                                status: AssessmentStatus.NON_ENCOUNTER
                            }
                        } else {
                            return current
                        }
                    })
                }
            }
        }
    }, [conference.status, booking.status, state.status])

    return <AssessmentContext.Provider value={state}>{children}</AssessmentContext.Provider>
}
